import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { AppNotification } from "../types";
import api from "../utils/api";

interface NotificationContextType {
    notifications: AppNotification[];
    unreadCount: number;
    loading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotifications must be used within a NotificationProvider");
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchNotifications = useCallback(async () => {
        if (!currentUser) return;
        try {
            setLoading(true);
            const data = await api.get<{ success: boolean; notifications: AppNotification[]; unreadCount: number }>(
                "/api/notifications"
            );
            if (data.success) {
                setNotifications(data.notifications);
                setUnreadCount(data.unreadCount);
            }
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    const fetchUnreadCount = useCallback(async () => {
        if (!currentUser) return;
        try {
            const data = await api.get<{ success: boolean; unreadCount: number }>(
                "/api/notifications/unread-count",
                { skipErrorToast: true }
            );
            if (data.success) {
                // If unread count changed, fetch full notifications
                if (data.unreadCount !== unreadCount) {
                    setUnreadCount(data.unreadCount);
                    fetchNotifications();
                }
            }
        } catch {
            // Silently fail on polling
        }
    }, [currentUser, unreadCount, fetchNotifications]);

    const markAsRead = useCallback(async (id: string) => {
        try {
            const data = await api.patch(`/api/notifications/${id}/read`, {});
            if (data.success) {
                setNotifications((prev) =>
                    prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
                );
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            const data = await api.patch("/api/notifications/read-all", {});
            if (data.success) {
                setNotifications((prev) =>
                    prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
                );
                setUnreadCount(0);
            }
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
        }
    }, []);

    const deleteNotification = useCallback(async (id: string) => {
        try {
            const data = await api.delete(`/api/notifications/${id}`);
            if (data.success) {
                setNotifications((prev) => {
                    const removed = prev.find((n) => n.id === id);
                    if (removed && !removed.isRead) {
                        setUnreadCount((c) => Math.max(0, c - 1));
                    }
                    return prev.filter((n) => n.id !== id);
                });
            }
        } catch (error) {
            console.error("Failed to delete notification:", error);
        }
    }, []);

    // Fetch notifications on mount and when user changes
    useEffect(() => {
        if (currentUser) {
            fetchNotifications();
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [currentUser, fetchNotifications]);

    // Poll for new notifications every 30 seconds
    useEffect(() => {
        if (currentUser) {
            pollingRef.current = setInterval(fetchUnreadCount, 30000);
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, [currentUser, fetchUnreadCount]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                fetchNotifications,
                markAsRead,
                markAllAsRead,
                deleteNotification,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};